import { createQueryKeys } from '@lukemorales/query-key-factory'
import { useMutation, useQuery, keepPreviousData } from '@tanstack/react-query'

import {
  queryClient,
  type MutationHook,
  type QueryHook,
} from '@/lib/react-query'

import { getRundownId } from '@/features/rundown/data/rundown.store'

import type { MentionDto } from '../model/dtos'
import type {
  GetMentionsParams,
  GetMentionParams,
  CreateMentionParams,
  UpdateMentionParams,
  DeleteMentionParams,
} from '../model/params'

import {
  createMention,
  deleteMention,
  getMention,
  getMentions,
  updateMention,
} from './requests'

export const mentionsQueryKeys = createQueryKeys('MENTIONS', {
  list: ({ rundownId, ...params }: GetMentionsParams) => [rundownId, params],
  byMentionId: ({ rundownId, mentionId, ...params }: GetMentionParams) => [
    rundownId,
    mentionId,
    params,
  ],
})

export const useMentions = ({
  params,
  options,
}: QueryHook<MentionDto[], GetMentionsParams>) => {
  return useQuery({
    queryKey: mentionsQueryKeys.list(params).queryKey,
    queryFn: async () => {
      const { data } = await getMentions(params)

      return data
    },
    staleTime: Number.POSITIVE_INFINITY,
    placeholderData: keepPreviousData,
    ...options,
  })
}

export const useMention = ({
  params,
  options,
}: QueryHook<MentionDto, GetMentionParams>) => {
  return useQuery({
    queryKey: mentionsQueryKeys.byMentionId(params).queryKey,
    queryFn: async () => {
      const { data } = await getMention(params)

      return data
    },
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    placeholderData: keepPreviousData,
    ...options,
  })
}

export const useCreateMention = ({
  options,
}: MutationHook<MentionDto, CreateMentionParams> = {}) => {
  return useMutation({
    mutationFn: async (params) => {
      const { data } = await createMention(params)
      return data
    },
    ...options,
  })
}

export const useUpdateMention = ({
  options,
}: MutationHook<MentionDto, UpdateMentionParams> = {}) => {
  return useMutation({
    mutationFn: async (params) => {
      const { data } = await updateMention(params)

      return data
    },
    ...options,
  })
}

export const useDeleteMention = ({
  options,
}: MutationHook<unknown, DeleteMentionParams> = {}) => {
  return useMutation({
    mutationFn: async (params) => {
      const { data } = await deleteMention(params)

      return data
    },
    ...options,
  })
}

/**
 * Get a mention from mentions cache in tanstack query.
 * Works outside React / no `useMention` needed.
 *
 * @remarks
 *   Requires a loaded rundown: throws via `getRundownId` when none is loaded.
 *   Temp. helper until migration.
 */
export function getMentionFromCache(mentionId: GetMentionParams['mentionId']) {
  const params = {
    rundownId: getRundownId(),
    mentionId: mentionId,
  }

  return queryClient.getQueryData<MentionDto>(
    mentionsQueryKeys.byMentionId(params).queryKey,
  )
}
